import React, { useState, useCallback } from 'react';

import PageLayout from '@app/layouts/pageLayout/PageLayout';
import GalleryList from '@pages/Gallery/GalleryList/GalleryList';
import YearlyGalleryView from '@pages/Gallery/components/YearlyGalleryView';
import MonthlyGalleryView from '@pages/Gallery/components/MonthlyGalleryView';
import MotionWrapper from '@components/wrapper/MotionWrapper';
import { getPathKey } from '@utils/getPath';
import { useRecoilState } from 'recoil';
import { modalOpenState } from '@atom/modal/modal';
import UpdateGalleryModal from './UpdateGallery/UpdateGalleryModal';
import PageControlSec from '@template/ControlSec/PageControlSec';
import usePageSet from '@hooks/usePageSet';
import HashtagTab from '@components/dataDisplay/hashtag/HashtagTab';
import Button from '@components/dataEntry/button/Button';
import { IgalleryItem } from '@typings/pages/gallery.interface';
import { AUTH_TYPE } from '@typings/common/authorizationType';
import { useGetSearchResult } from '@hooks/getSearchResult';
import { formatPixelValue } from '@utils/formatPixelValue';
import { PAGE_LAYOUT_TYPE } from '@typings/pages/pageLayout.interface';
import { useFilterLayoutType } from '@hooks/useFilterLayoutType';

function GalleryPage() {
  const [isEdit, setIsEdit] = useState(false);
  const collectionPath = getPathKey(1);
  const [openModal, setOpenModal] = useRecoilState(modalOpenState);
  const [list, setList] = useState<IgalleryItem[] | any>();
  const [selectedMonth, setSelectedMonth] = useState<{
    year: number;
    month: number;
    list: IgalleryItem[];
  } | null>(null);

  const handleEdit = useCallback(() => setIsEdit(!isEdit), [isEdit]);

  // page Setting --------------------------------------------------------
  const {
    docLayout,
    orderBy,
    orderDir,
    docWidth,
    docLine,
    isExtendsModal,
    writeAuthType,
    docLimit,
    allCount,
    hashtagType,
    contentHeight,
    isRealtimeEnabled,
  } = usePageSet({
    collectionPath,
  });
  const [refreshKey, setRefreshKey] = useState(0);
  // 🔧 검색 훅: docLimit을 hitsPerPage로 전달하여 페이지네이션 지원
  const { 
    searchResultData, 
    searchError, 
    isSearching,
    paginationInfo: searchPaginationInfo,
    goToSearchPage,
  } = useGetSearchResult({ 
    collectionPath, 
    orderBy, 
    orderDir,
    hitsPerPage: docLimit || 20,
  });

  const [docType, pageLayoutMap] = useFilterLayoutType({
    docLayout,
    pageSetting: [
      PAGE_LAYOUT_TYPE.DEFAULT,
      PAGE_LAYOUT_TYPE.MASONRY,
      PAGE_LAYOUT_TYPE.YEARLY,
      PAGE_LAYOUT_TYPE.MONTH_GROUP,
    ],
    defaultLayout: PAGE_LAYOUT_TYPE.DEFAULT,
  });

  const handleOpenModal = () => setOpenModal(true);

  const handleBackToList = useCallback(() => {
    setSelectedMonth(null);
  }, []);

  const renderList = () => {
    if (selectedMonth) {
      return (
        <div style={{ marginTop: 32 }}>
          <Button shape="stroke" onClick={handleBackToList}>
            &lt; 목록으로 돌아가기
          </Button>
          <div style={{ marginTop: 12, marginBottom: 8 }}>
            <h4 style={{ margin: 0, fontWeight: 800 }}>
              {selectedMonth.year}년 {selectedMonth.month.toString().padStart(2, '0')}월
            </h4>
          </div>
          <GalleryList
            docLayout={PAGE_LAYOUT_TYPE.DEFAULT}
            isExtendsModal={isExtendsModal || false}
            line={docLine || 4}
            list={selectedMonth.list}
            isEdit={isEdit}
            collectionPath={collectionPath}
            contentHeight={contentHeight || '340px'}
          />
        </div>
      );
    }

    return (
      <HashtagTab
        collectionPath={collectionPath}
        setList={setList}
        orderDir={orderDir || 'desc'}
        orderBy={orderBy || 'createdAt'}
        searchError={searchError}
        isSearching={isSearching}
        searchResultData={searchResultData}
        searchPaginationInfo={searchPaginationInfo}
        goToSearchPage={goToSearchPage}
        list={
          docType === PAGE_LAYOUT_TYPE.YEARLY ? (
            <YearlyGalleryView
              items={searchResultData || list || []}
              isEdit={isEdit}
              collectionPath={collectionPath}
              line={docLine || 4}
              mobileLine={2}
              onSelectMonth={({ year, month, list }) => {
                setSelectedMonth({ year, month, list });
              }}
            />
          ) : docType === PAGE_LAYOUT_TYPE.MONTH_GROUP ? (
            <MonthlyGalleryView
              items={searchResultData || list || []}
              isEdit={isEdit}
              collectionPath={collectionPath}
              contentHeight={contentHeight || '340px'}
              line={docLine || 6}
              mobileLine={2}
            />
          ) : (
            <GalleryList
              docLayout={docType}
              isExtendsModal={isExtendsModal || false}
              line={docLine || 4}
              list={searchResultData || list}
              isEdit={isEdit}
              collectionPath={collectionPath}
              contentHeight={contentHeight || '340px'}
            />
          )
        }
        docLimit={docLimit}
        allCount={allCount}
        hashtagLayout={hashtagType}
        isRealtimeEnabled={isRealtimeEnabled}
        refreshTrigger={refreshKey}
      />
    );
  };

  return (
    <>
      <MotionWrapper>
        <PageLayout style={{ maxWidth: formatPixelValue(docWidth, '100%') }}>
          <PageControlSec
            uploadText="추가"
            editText={!isEdit ? '편집' : '편집 완료'}
            handleEdit={handleEdit}
            handleUpload={handleOpenModal}
            writeAuthType={writeAuthType || AUTH_TYPE.MASTER}
            hashtagLayout={hashtagType}
            pageLayoutSetting={pageLayoutMap}
            showLine
            showWidth
            showLimit
            showSortOrder
            showModalLayout
            showContentsHeight
            showHashtagType
            isRealtimeEnabled={isRealtimeEnabled}
            onRefresh={() => setRefreshKey((k) => k + 1)}
          />

          {renderList()}
        </PageLayout>
      </MotionWrapper>
      {openModal && (
        <UpdateGalleryModal docId={''} close={() => setOpenModal(false)} />
      )}
    </>
  );
}

export default GalleryPage;
